// importing storymaps default data
import storyMapDefaultSettings from '../content/storymaps/storymap-default-settings.json'
import { getBinForValue, hslaRemoveAlpha } from './utilsData'
import {STORY_HEADER_OPTIONS} from '../constants'

const defaultStoryProps = {
	VIEWPORT_OPTIONS: [],
	layers: [],
	scenes: []
}

const DEFAULT_VALUES = { lat: 44, lon: -72 } /* somewhere in NH... */

class LayerInfo {
	constructor({dataLayer, fieldName, fieldRange, fieldBins, color, addCustomFilters}) {
		this._dataLayer = dataLayer
		this._fieldName = fieldName
		this._fieldRange = fieldRange
		this._fieldBins = fieldBins
		this._color = color
		this._customFilters = addCustomFilters

		this._paintProperty = null
	}

	get dataLayer() { return this._dataLayer }
	get fieldName() { return this._fieldName }
	get fieldRange() { return this._fieldRange }
	get fieldBins() { return this._fieldBins }
	get customFilters() { return this._customFilters }
	get color() { return this._color }
	get isStyled() { return this._paintProperty !== null }

	get paint() {
		return this._paintProperty.paint || {}
	}

	get paintProperty() {
		return this._paintProperty
	}

	shouldBeStyled = () => {
		// if there are no specified colors or filters for this layer
		// then we don't inject new painting style into it
		return this._color || this._customFilters
	}

	// Swap in layer CARTO IDs throughout for nameInCode (eg swap "county_data_5" for "countyData")
	convertLayerName = layerNameConverter => {
		this._dataLayer = layerNameConverter(this._dataLayer)
	}

	updateFilters = newFilters => {
		this._customFilters = newFilters
	}

	updateColor = newColor => {
		this._color = newColor
	}

	addPaintStyling = paintProperty => {
		this._paintProperty = paintProperty
	}

	// Apply a template layerInfo to any null values
	// Fix templates such that they don't overwrite any intentionally set-blank fields?
	applyTemplate = templateInfo => {
		this._dataLayer = this._dataLayer || templateInfo.dataLayer
		this._fieldName = this._fieldName || templateInfo.fieldName
		this._fieldRange = this._fieldRange || templateInfo.fieldRange
		this._fieldBins = this._fieldBins || templateInfo.fieldBins
		this._color = this._color || templateInfo.color
		this._customFilters = this._customFilters || templateInfo.customFilters
	}
}

class DashboardComponentInfo {
	constructor({type, text, dataLayer, fieldName, dataOptions, title, colorTheme, dataLayers, fields = []}) {
		this._type = type
		this._rawText = text
		this._dataLayer = dataLayer
		this._fieldName = fieldName
		this._display = {
			isReady: (dataLayer && fieldName) ? false : true,
			formattedText: text
		}
		this._dataOptions = dataOptions
		this._title = title
		this._colorTheme = colorTheme
		
		// COMMUNITY_SUMMARY
		this._fields = fields
		

		// legend component
		this._dataLayers = dataLayers
	}

	get type() { return this._type }
	get rawText() { return this._rawText }
	get dataLayer() { return this._dataLayer }
	get fieldName() { return this._fieldName }
	get display() { return this._display }
	get fields() { return this._fields }
	get dataLayers() { return this._dataLayers }
	get dataOptions() { return this._dataOptions }
	get title() { return this._title }
	
}

class DashboardInfo {
	constructor({type, components, showAllComponentsOnStart = false}) {
		this._type = type
		this._showAllComponentsOnStart = showAllComponentsOnStart
		this._components = components.map(co => new DashboardComponentInfo(co))

		this._dataLayers = components.filter(co => co.dataLayer && co.fieldName).map(({dataLayer, fieldName}) => {
			return {dataLayer: dataLayer, fieldName: fieldName}
		})
	}

	get type() { return this._type }
	get showAllComponentsOnStart() { return this._showAllComponentsOnStart }
	get components() { return this._components }
	get isReady() { return this._components.every(co => co.display.isReady ) }
}

class StorySceneInfo {
	constructor({info, viewport, viewportLevel, dataLayers, title, refCallbacks, applyAllFilters, mapMenuOptionToOpenOnSceneStart, sceneSpecificGeography}) {
		this._info = info || {}
		this._viewport = viewport
		this._viewportLevel = viewportLevel
		this._title = title
		this._mapMenuOptionToOpenOnSceneStart = mapMenuOptionToOpenOnSceneStart
		this._sceneSpecificGeography = sceneSpecificGeography

		// process and set up all of the externally referenced variables that represents a scene
		// primarily list of visible layers to render and paint props for stylized layers
		this._dataLayers = []
		this._visibleLayers = []
		this._styledLayersList = {}
		this._processDataLayers(dataLayers, refCallbacks)
	}

	get viewport () { return this._viewport }
	get title () { return this._title }
	get mapMenuOptionToOpenOnSceneStart () { return this._mapMenuOptionToOpenOnSceneStart }
	get sceneSpecificGeography () { return this._sceneSpecificGeography }

	// Lookup table {x: true} for all dataLayers
	// Notably all data layers specified in YAML will be the only visible layers
	get visibleLayers () {
		return this._visibleLayers
	}

	getLayerById = layerId => {
		const layersWithId = this._dataLayers.filter(d => d.dataLayer === layerId)
		return layersWithId.length > 0 ? layersWithId[0] : null
	}

	getLayerNamesInOrder = () => {
		return this._dataLayers.map(d => d.dataLayer)
	}

	getLayersWithFields = (styledLayersOnly = false) => {
		// filter to specified layers
		var layersWithFields = this._dataLayers.filter(d => d.fieldName && (!styledLayersOnly || d.isStyled))

		// return list with dataLayer/fieldName objs
		return layersWithFields.map(d => {return {dataLayer: d.dataLayer, fieldName: d.fieldName}})
	}

	colorLookup = (value, layerId) => {
		const layer = this.getLayerById(layerId)
		if (layer && layer.color) {
			const range = layer.fieldRange
			const bins = layer.fieldBins
			const binIndex = getBinForValue(value, range[0], range[1], bins)
			return hslaRemoveAlpha(layer.color.fillBins[binIndex])
		}
		return '#aaaaaa' // unsure, something went wrong
	}

	regeneratePaintProperties = (location) => {
		this._dataLayers.forEach(d => {
			if (d.isStyled)
				d.paintProperty.regeneratePaintProperties(location)
		})
	}

	_processDataLayers = (dataLayers, refCallbacks) => {
		this._dataLayers = dataLayers

		// create a lookup table {x: true} for all dataLayers to be used as visibleLayers
		const layersNamesMap = dataLayers.map(d => d.dataLayer).reduce(function(result, item) {
			result[item] = true
			return result
		}, {});
		this._visibleLayers = layersNamesMap

		// add paint properties/styled
		this._dataLayers.forEach(d => {
			if (d.shouldBeStyled()) {
				const paintProp = refCallbacks.createPaintProp(d)
				d.addPaintStyling(paintProp)
			}
		})
	}
}

export default class StoryDataManager {
	constructor(data) {
		// init variables
		this._location = {lat: null, lon: null} // current lat and lon coords
		this._sceneData = {} // this is the actual references to scene data and dashboards
		this._templates = {}
		const {storymap, layerNameToIdCallback, createLayerPaintPropertyCallback} = data

		// all the data from a storymap JSON
		this._rawdata = { ...defaultStoryProps, ...storymap }

		// callbacks to external functions
		this._ref = {
			layerNameToId: layerNameToIdCallback,
			createPaintProp: createLayerPaintPropertyCallback
		}

		// if starting location defined
		if (this.hasStartingLocation()) {
			const {lat, lon} = this.getStartingLocation()
			this._location = {lat: lat, lon: lon}
		}

		// load templates and then process the data
		this._loadTemplates(this._rawdata.templates)
		this._generate(this._location) // let's generate default data and viewports
	}

	_loadTemplates = (templates) => {
		if (templates && templates.dataTemplates) {
			templates.dataTemplates.forEach(info => {
				this._templates[info.TEMPLATE_ID] = new LayerInfo(info)
			})
		}
	}

	// PLEASE REMOVE THIS FUNCTION AFTER REMOVING OTHER REFERENCES
	// to no longer have getScenes()
	getScenes = () => {
		return Object.keys(this._sceneData).map(idx => this._sceneData[idx].scene)
	}

	getSceneAtSceneIndex = sceneIdx => {
		return this._sceneData[sceneIdx] ? this._sceneData[sceneIdx].scene : {}
	}

	getDashboardsAtSceneIndex = sceneIdx => {
		return this._sceneData[sceneIdx].dashboards
	}

	// predefined starting location
	hasStartingLocation = () => {
		const s = this._rawdata.startingLocation
		return s && s.fullLocation && s.stateName && s.lat && s.lon
	}

	getStartingLocation = () => {
		return this._rawdata.startingLocation
	}

	shouldSkipIntroScreen = () => {
		return this._rawdata.shouldSkipIntroScreen
	}
	
	clickingACommunityOnTheMapShouldCompareIt = () => {
		return this._rawdata.clickingACommunityOnTheMapShouldCompareIt
	}
	
	mapTitle = () => {
		return this._rawdata.mapTitle
	}
	
	sqlFilterToApplyToAllACSLayers = () => {
		return this._rawdata.sqlFilterToApplyToAllACSLayers || ''
	}

	getDashboardHeader = () => {
		return this._rawdata.setDashboardHeader || STORY_HEADER_OPTIONS.LOCATION
	}

	// Let's regenerate with new viewpoint data if location changes
	updateLocationForStoryScenes = location => {
		// regenerate data for the viewpoint location changes from saved
		const { lat, lon } = location || {}
		const { _lat, _lon } = this._location
		if (location && (_lat !== lat && _lon !== lon)) {
			this._generate(location)
		}
	}

	// Generate viewpoints and scene
	_generate = location => {
		location = location || this._location
		this._generateViewports(location)
		this._generateScenes(location)
	}

	// Generate viewports from storyMapDefaultSettings json file
	_generateViewports = ({lat = DEFAULT_VALUES.lat, lon = DEFAULT_VALUES.lon}) => {
		this._location = {lat: lat, lon: lon}

		this._viewports = {}
		storyMapDefaultSettings.VIEWPORT_OPTIONS.forEach(viewport => {
			const {id, ...viewportProps} = viewport
			this._viewports[id] = {
				...viewportProps
			}
			this._viewports[id].latitude = this._viewports[id].latitude || lat
			this._viewports[id].longitude = this._viewports[id].longitude || lon
		})
	}

	// First set to any template data and then override accordingly
	_formatDataLayersFromTemplates = dataLayers => {
		return dataLayers.map(rawdata => {
			const newInfo = new LayerInfo(rawdata)

			if (rawdata.dataFromTemplate && rawdata.dataFromTemplate in this._templates)
				newInfo.applyTemplate(this._templates[rawdata.dataFromTemplate])

			return newInfo
		})
	}

	// Swap in layer CARTO IDs throughout for nameInCode (eg swap "county_data_5" for "countyData")
	_convertLayerNamesToCartoIds = layers => {
		layers.forEach(layerInfo => { layerInfo.convertLayerName(this._ref.layerNameToId) })
	}

	_createStorySceneInfo = (scene, location) => {
		const { viewport, dataLayers = [], title, mapMenuOptionToOpenOnSceneStart, sceneSpecificGeography } = scene
		const formattedLayers = this._formatDataLayersFromTemplates(dataLayers)

		this._convertLayerNamesToCartoIds(formattedLayers)

		// return a new scene with corrected VisualDataInfo and cartoNames and viewport
		const newStoryScene = new StorySceneInfo({
			...scene,
			title: title,
			viewport: this._viewports[viewport], // Inject the lat lon etc appropriate for current location/zoom level
			mapMenuOptionToOpenOnSceneStart: mapMenuOptionToOpenOnSceneStart,
			sceneSpecificGeography: sceneSpecificGeography,
			viewportLevel: viewport,
			dataLayers: formattedLayers,
			refCallbacks: this._ref
		})
		// init with location information
		newStoryScene.regeneratePaintProperties(location)

		return newStoryScene
	}

	_createDashboardsForScene = scene => {
		if (scene.dashboards) {
			// parse the dashboard raw YAML for any data we want to format
			return scene.dashboards.map(dashboard => {
				const _components = dashboard.components.map(co => {
					const dataOptions = co.dataOptions ? co.dataOptions.map(o => {
						return { ...o, dataLayer: this._ref.layerNameToId(o.dataLayer)}
					}) : null

					return {
						...co,
						dataLayer: this._ref.layerNameToId(co.dataLayer), // fix data layer names
						dataLayers: co.dataLayers ? this._formatDataLayersFromTemplates(co.dataLayers) : null,
						dataOptions: dataOptions
					}
				})
				// if (scene.subtitle) {
				// 	_components.unshift({ type: 'TEXT_COMPONENT', text: scene.subtitle })
				// }
				// if (scene.title) {
				// 	_components.unshift({type: 'TITLE_COMPONENT', text: scene.title})
				// }

				return new DashboardInfo({
					...dashboard,
					components: _components
				})
			})
		}

		return []
		// helper for authors to create simple scenes
		// else if (scene.title) {
		// 	const components = [{ type: 'TITLE_COMPONENT', text: scene.title }]
		// 	if (scene.subtitle)
		// 		components.push({ type: 'TEXT_COMPONENT', text: scene.subtitle })
		// 	return [new DashboardInfo({ type: 'LEFT_PANEL', components: components })]
		// }
	}

	// Generate scenes from the passed in story map json
	_generateScenes = location => {
		const prev = Object.keys(this._sceneData).length > 0 ? this._sceneData : null
		this._sceneData = {}

		// if (!this.hasStartingLocation()) {
		// 	// manually prepending national selector scene
		// 	this._sceneData[0] = {
		// 		scene: this._createStorySceneInfo(storyMapDefaultSettings.NATIONAL_SCENE_FOR_SELECTING_LOCATION),
		// 		dashboards: prev ? prev[0].dashboards : this._createDashboardsForScene(storyMapDefaultSettings.NATIONAL_SCENE_FOR_SELECTING_LOCATION)
		// 	}
		// }

		// add remaining scenes keyed by order index, maybe skipping first for national
		const skip = Object.keys(this._sceneData).length
		this._rawdata.scenes.forEach((scene, index) => {
			this._sceneData[index + skip] = {
				scene: this._createStorySceneInfo(scene, location),
				dashboards: prev ? prev[index + skip].dashboards : this._createDashboardsForScene(scene)
			}
		})
	}

	// Handy
	//debugPrint = () => { console.log(this._rawdata) }
}

